/**
 * Temporary screen-scoped design tokens for **Web Forms** (`/webforms`).
 *
 * The header mirrors Sign Requests 1:1 — the two screens sit on the same
 * centred column and must read as one family, so the title block and the
 * primary action reuse its values. Once every screen is re-skinned these get
 * promoted into `src/theme` and this file is deleted.
 */

import { button, contentCard, inkfree, notification, signer, white } from "theme/colors";

export const webFormsTokens = {
  /**
   * Inner gutters of the content column. The column itself is centred by
   * `CenteredAuthenticatedLayout`, the same wrapper Sign Requests uses.
   */
  page: {
    paddingTop: "32px",
    paddingX: "40px",
    paddingBottom: "48px",
  },

  /** "Web Forms" title block + the single header action. */
  header: {
    gap: "12px",
    marginBottom: "22px",
    titleColor: signer.ink,
    titleFontSize: "26px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    subtitleColor: signer.text.subtitle,
    subtitleFontSize: "13.5px",
    subtitleMarginTop: "5px",
  },

  /**
   * Filled "Create Web Form" button. Sign Requests draws this as a split
   * button; Web Forms has a single create path, so the caret half is dropped
   * and the radius closes on both ends.
   */
  /** Solid fill tracks the app brand colour, not the design's orange — see
   * `theme/colors.ts` §11.5 "orange fills → brand red". */
  primaryButton: {
    bg: inkfree.main,
    hoverBg: inkfree.dark,
    color: white,
    radius: "10px",
    padding: "12px 18px",
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "9px",
    iconSize: 16,
  },

  /**
   * The list card. Chrome, the grey column-header strip and the closing footer
   * come from Settings → Roles, which is the reference table; the row internals
   * (icon tile, title over subtitle, kebab) follow the Sign Requests rows so
   * the two lists read alike.
   */
  table: {
    bg: white,
    border: contentCard.borderMuted,
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",

    /** NAME | TEMPLATE | CREATED | ⋮ — shared by the strip and every row. */
    gridTemplateColumns: "minmax(0,1fr) 200px 230px 32px",
    gap: "18px",
    /** Horizontal gutter shared by the strip, the rows and the footer. */
    gutter: "24px",

    columnHeaderBg: signer.surface.subtle,
    columnHeaderBorder: signer.border.light,
    columnHeaderColor: signer.text.faint,
    columnHeaderFontSize: "10.5px",
    columnHeaderFontWeight: 700,
    columnHeaderLetterSpacing: "0.09em",
    columnHeaderPaddingY: "10px",

    rowPaddingY: "16px",
    rowDivider: signer.border.divider,
    rowHoverBg: signer.surface.hover,
    /** Icon tile plus the text block beside it. */
    leadGap: "13px",
    iconTileSize: 40,
    iconTileRadius: "9px",
    iconSize: 18,
    titleFontSize: "14px",
    titleFontWeight: 700,
    titleColor: signer.ink,
    subFontSize: "12px",
    subColor: signer.text.faint,
    subMarginTop: "3px",
    /** Every web form is reachable by link; the row says so under its name. */
    subLabel: "Shareable signing link",

    chipGap: "7px",
    chipPadding: "5px 11px",
    chipRadius: "999px",
    chipDotSize: 7,
    chipFontSize: "12px",
    chipFontWeight: 700,

    createdFontSize: "12.5px",
    createdLabelColor: signer.text.faint,
    createdValueColor: signer.ink,
    createdValueFontWeight: 700,

    kebabSize: 32,
    kebabRadius: "8px",
    kebabColor: signer.text.subtitle,
    kebabIconSize: 18,
    kebabHoverBg: signer.border.divider,
    kebabHoverColor: signer.ink,

    /** Strip closing the card — the list loads on scroll, so it has no pager. */
    footerPaddingY: "14px",
    footerGap: "8px",
    footerFontSize: "12.5px",
    footerColor: signer.text.faint,
    footerValueColor: signer.text.meta,
    footerValueFontWeight: 700,
    footerIconSize: 14,
    footerSpinnerSize: 14,
    footerSpinnerThickness: 4,

    emptyPaddingY: "56px",
    emptyTitleFontSize: "14px",
    emptyTitleFontWeight: 600,
    emptyTitleColor: signer.text.meta,
    emptySubFontSize: "12.5px",
    emptySubMarginTop: "4px",
    emptySubColor: signer.text.faint,
  },
} as const;

/**
 * Raw media query for the narrow phone column of
 * `Webforms - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375 and 390 differently — the standard column parks a
 * square Create button beside the count line, while the narrow one has no room
 * for it and floats a "Create" pill instead. MUI has no breakpoint between the
 * two, so the split is expressed as a raw query and applied *after* the `xs`
 * values so it wins.
 */
export const PHONE_SM_QUERY = "@media (max-width:389.98px)";

/**
 * Phone (`xs`) and tablet (`sm`) values, traced from
 * `Webforms - Mobile & Tablet.dc.html`.
 *
 * Only the values that actually differ from the desktop tokens above live
 * here; anything omitted is inherited, so the desktop screen is untouched.
 * `sm` / `std` inside `phone` are the 375 and 390 columns of the reference.
 */
export const webFormsResponsiveTokens = {
  phone: {
    /** Content column: 14px gutters at 375, 16px at 390. */
    paddingX: { sm: "14px", std: "16px" },
    paddingTop: { sm: "14px", std: "16px" },
    paddingBottom: { sm: "32px", std: "34px" },
    /** Vertical rhythm between the count line, the list and the tally. */
    gap: { sm: "13px", std: "14px" },

    /** "<b>8</b> web forms" — the reference's only page-body header text. */
    subtitleFontSize: { sm: "12.5px", std: "13px" },
    subtitleLineHeight: 1.45,

    /**
     * Full-width "Create" button under the header. Same metrics as Sign
     * Requests and Templates so the three read as one control.
     */
    createButton: {
      padding: "13px 14px",
      radius: "11px",
      fontSize: "14px",
      gap: "9px",
      iconSize: 16,
    },

    /** Every form is its own card; the list card's chrome is dropped. */
    list: { gap: "10px" },

    card: {
      border: contentCard.border,
      radius: "14px",
      padding: { sm: "13px 14px", std: "14px 16px" },
      /** Between the head line and the template chip below it. */
      gap: { sm: "10px", std: "11px" },
      /** Between the icon tile and the text block beside it. */
      headGap: { sm: "11px", std: "12px" },

      iconTileSize: { sm: 38, std: 40 },
      iconTileRadius: "10px",
      iconSize: { sm: 17, std: 18 },

      titleFontSize: { sm: "13px", std: "13.5px" },
      subFontSize: { sm: "11px", std: "11.5px" },
      subMarginTop: "2px",
      /** The date moves under the name, so the caption changes with it. */
      subPrefix: "Created ",

      kebabSize: { sm: 30, std: 32 },
      kebabRadius: { sm: "8px", std: "9px" },
      kebabIconSize: { sm: 17, std: 18 },

      chipPadding: { sm: "4px 10px", std: "5px 11px" },
      chipFontSize: { sm: "11px", std: "11.5px" },
      chipGap: "6px",
      chipDotSize: 6,
    },

    empty: {
      radius: "14px",
      padding: { sm: "44px 20px", std: "48px 20px" },
      titleFontSize: { sm: "14.5px", std: "15px" },
      subFontSize: { sm: "12px", std: "12.5px" },
      iconSize: { sm: 56, std: 60 },
      iconGlyphSize: { sm: 24, std: 25 },
      iconMarginBottom: { sm: "14px", std: "15px" },
    },

    /** The tally sits on the page below the list, not inside a card. */
    footer: { fontSize: { sm: "11.5px", std: "12px" } },
  },

  tablet: {
    paddingX: "26px",
    paddingTop: "22px",
    paddingBottom: "40px",
    gap: "18px",

    titleFontSize: "24px",
    subtitleFontSize: "13.5px",
    subtitleMarginTop: "6px",
    headerGap: "20px",

    primaryButton: {
      height: 44,
      padding: "0 20px",
      radius: "10px",
      fontSize: "13.5px",
      gap: "9px",
      iconSize: 16,
    },

    card: {
      border: contentCard.border,
      radius: "14px",
      shadow: "0 1px 3px rgba(33,21,71,.05)",
    },

    row: {
      /** NAME | TEMPLATE | CREATED | ⋮ — narrower tracks than the desktop. */
      gridTemplateColumns: "minmax(0,1fr) 150px 170px 40px",
      gap: "16px",
      paddingY: "15px",
      gutter: "20px",
      leadGap: "13px",
      iconTileSize: 40,
      iconTileRadius: "9px",
      iconSize: 19,
      titleFontSize: "14px",
      subFontSize: "12px",
      /** The tablet prints the bare timestamp — no "Created on" prefix. */
      createdFontSize: "12px",
      createdColor: signer.text.subtitle,
      kebabSize: 34,
      kebabRadius: "9px",
      kebabIconSize: 18,
      kebabColor: signer.text.faint,
    },

    empty: {
      paddingY: "80px",
      titleFontSize: "16px",
      subFontSize: "13px",
      subMaxWidth: "420px",
      iconSize: 64,
      iconGlyphSize: 26,
      iconMarginBottom: "18px",
    },

    footer: { fontSize: "12.5px" },
  },
} as const;

/**
 * Wash behind the empty state's glyph. The reference draws it on every
 * viewport below `md`; the desktop card has no icon at all.
 */
export const webFormsEmptyIcon = {
  bg: signer.orange.tint,
  stroke: signer.orange.main,
} as const;

/** Colour the tally takes once it sits on the page rather than in the card. */
export const WEBFORMS_PAGE_FOOTER_COLOR = button.disabledColor;

/**
 * Per-template colouring for the row's icon tile and its template chip. Which
 * tone a row gets is derived from the template name (see `getTemplateTone`), so
 * every form built from the same template is tinted the same way.
 */
export const webFormsTemplateTones = [
  {
    bg: signer.orange.hover,
    stroke: signer.orange.main,
    chipBg: inkfree.light,
    chipColor: signer.orange.dark,
  },
  { bg: "#EDEBFB", stroke: "#6C5CE7", chipBg: "#EFECFC", chipColor: "#5544C0" },
  {
    bg: notification.success.bg,
    stroke: "#12917A",
    chipBg: notification.success.bg,
    chipColor: "#0E7561",
  },
  { bg: "#EAF2FC", stroke: "#2A7DE1", chipBg: "#EAF2FC", chipColor: "#1B5DAC" },
] as const;

export type WebFormsTemplateTone = (typeof webFormsTemplateTones)[number];

export default webFormsTokens;
